import React, { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

export default function ManagerRemarksDialog({ lead, onOpenChange }) {
  const { user } = useAuth();
  const canAdd = user.role === "sales_manager";
  const [remarks, setRemarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!lead) return;
    setText("");
    setLoading(true);
    api.get(`/leads/${lead.id}/manager-remarks`)
      .then(({ data }) => setRemarks(data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [lead]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/manager-remarks`, { text });
      setRemarks((r) => [data, ...r]);
      setText("");
      toast.success("Remark added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(lead)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="manager-remarks-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Sales Manager Remarks</DialogTitle>
          <DialogDescription>
            {lead?.customer_name} · Event {lead?.event_date}. Visible to Sales Manager & Management only.
          </DialogDescription>
        </DialogHeader>
        {canAdd && (
          <form onSubmit={add} className="space-y-2">
            <Textarea required rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a remark…" data-testid="manager-remark-input" />
            <div className="flex justify-end">
              <Button type="submit" disabled={saving || !text.trim()} className="bg-[#1E3A8A] hover:bg-[#172554]" data-testid="manager-remark-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1.5" /> Add Remark</>}
              </Button>
            </div>
          </form>
        )}
        <div className="max-h-80 overflow-y-auto space-y-3 pr-1" data-testid="manager-remarks-list">
          {loading ? (
            <div className="py-6 flex justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : remarks.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400" data-testid="manager-remarks-empty">No remarks yet</p>
          ) : (
            remarks.map((r) => (
              <div key={r.id} className="border-l-2 border-[#1E3A8A] bg-slate-50 rounded-r-md px-3 py-2" data-testid="manager-remark-item">
                <p className="text-sm text-slate-800 whitespace-pre-wrap">{r.text}</p>
                <p className="text-[11px] text-slate-400 mt-1">{r.author_name} · {fmt(r.created_at)}</p>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
